import { useCallback, useEffect, useState } from 'react';
import { SESSION_LIMITS, MAX_PASSPHRASE, MIN_PASSPHRASE, SocialAuthError, cancellationOf, mayCancelSession, mayRekeyStudy } from '@versesverse/core';
import type { NewSession, SessionAccess, SessionView } from '@versesverse/core';
import { useLang } from '../i18n/lang';
import { HallPicker, useMyHalls } from './HallPicker';
import type { HallMembershipReader } from './HallPicker';
import { sessionWhenText, useMinuteTick } from './sessionTime';
import { refusalWords } from '../i18n/refusal';
import { useOneAtATime } from './useOneAtATime';

/** What the sessions panel reads off the social client. */
export interface SessionsClient extends HallMembershipReader {
  listSessions(nodeId: string): Promise<SessionView[]>;
  /** Everything this reader has promised, anywhere — signed-in, and empty for everyone else. */
  mySessions(): Promise<SessionView[]>;
  scheduleSession(nodeId: string, session: NewSession): Promise<SessionView>;
  cancelSession(sessionId: string): Promise<SessionView>;
  /** A new word for a study held behind one — the rotation a room has had since it could lock. */
  rekeyStudy(sessionId: string, passphrase: string): Promise<SessionView>;
  signInUrl(): string;
}

export interface SessionsProps {
  client: SessionsClient;
  nodeId: string;
  /** The node's name, shown for a session nobody named — never stored on the session (D10). */
  nodeName: string;
  /** Our own user id, so a leader sees the controls over their own sessions. Null when signed out. */
  meId: string | null;
  /** Names a node we are NOT standing on — for the reader's own sessions elsewhere on the map. */
  nodeNameOf(nodeId: string): string;
  /** Join the room a live session opened. The rooms panel owns joining; this only points at it. */
  onJoin(roomId: string): void;
  /** Go to another node — how a reader reaches a study of their own that is somewhere else. */
  onGoToNode(nodeId: string): void;
}

/*
 * WHAT IS COMING UP ON THIS NODE.
 *
 * `GROWTH.md §4` calls calendared activity the product's scarcest resource: a room with nobody in
 * it is a promise visibly unkept, and the thing that reliably puts people in a room at a known time
 * is a session somebody scheduled. So a quiet node is not an empty page — it says what is coming,
 * and offers to add to it.
 *
 * Reading needs no account, because a session IS the invitation. Scheduling one needs a reader.
 */
/**
 * The lengths a study may run, in minutes.
 *
 * A short list rather than a number box: a leader knows the length of their own meeting, and every
 * calendar asks this way. Bounded by SESSION_LIMITS.maxDurationMinutes, which the server clamps to
 * anyway — these are the four a study is actually ever scheduled for.
 */
const SESSION_LENGTHS = [30, 60, 90, 120] as const;

export function Sessions({ client, nodeId, nodeName, meId, nodeNameOf, onJoin, onGoToNode }: SessionsProps): React.JSX.Element {
  const { t, lang } = useLang();
  const [sessions, setSessions] = useState<SessionView[]>([]);
  const [unread, setUnread] = useState(false);
  /** The reader's own, across the whole map — see `elsewhere` below for why only some are shown. */
  const [mine, setMine] = useState<SessionView[]>([]);
  /**
   * WHETHER THE READER'S OWN STUDIES COULD BE READ AT ALL.
   *
   * `mine` carries the one notice a leader has no other way to get: a study called off **over their
   * head**, because the community they held it under was deleted or barred them. The panel below
   * exists because that used to be *"no event, no message, nothing to read"* — and a failed read put
   * it straight back, silently, with a leader still expecting their Thursday study to happen.
   */
  const [mineUnread, setMineUnread] = useState(false);
  const [when, setWhen] = useState('');
  const [title, setTitle] = useState('');
  const [weekly, setWeekly] = useState(false);
  /** How long the study runs. An hour unless a leader says otherwise — see the control below. */
  const [minutes, setMinutes] = useState(60);
  /** Which door the room it opens will have (D24), and the word for the one that needs one. */
  const [access, setAccess] = useState<SessionAccess>('public');
  const [passphrase, setPassphrase] = useState('');
  /** Which study is having its word changed, and the new one. Null while nobody is doing that. */
  const [rekeying, setRekeying] = useState<string | null>(null);
  const [newWord, setNewWord] = useState('');
  /** Which Hall this study belongs to. Empty string is the commons. */
  const [underHall, setUnderHall] = useState('');
  const myHalls = useMyHalls(client, meId);
  /* A clock that moves, so "in 12 minutes" is not frozen at the moment the panel drew — see the
     hook, which the Hall page uses for the same reason. */
  const minuteTick = useMinuteTick();
  const [busy, setBusy] = useState(false);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<SessionView[]> => client.listSessions(nodeId), [client, nodeId]);

  const refreshMine = useCallback(async (): Promise<SessionView[]> => (
    meId === null ? [] : client.mySessions()
  ), [client, meId]);

  useEffect(() => {
    let live = true;
    setNeedsSignIn(false);
    setProblem(null);
    refresh()
      .then(found => { if (live) { setSessions(found); setUnread(false); } })
      /* "Nothing scheduled here yet" is false and alarming for a leader who scheduled one. */
      .catch(() => { if (live) setUnread(true); });
    refreshMine()
      .then(found => { if (live) { setMine(found); setMineUnread(false); } })
      .catch(() => { if (live) setMineUnread(true); });
    return () => { live = false; };
  }, [refresh, refreshMine]);

  /*
   * WHAT THIS READER HAS PROMISED SOMEWHERE ELSE.
   *
   * Only the ones NOT on this node, because the list above already shows those and a leader
   * reading their own study twice learns nothing. `GET sessions/mine` answered this from the day
   * sessions were built and no client asked, which left the cap unanswerable: a leader refused with
   * *"cancel one of your 5 scheduled sessions"* had no way to find the five except by walking back
   * to every node they could remember standing on.
   */
  const elsewhere = mine.filter(session => session.nodeId !== nodeId && cancellationOf(session) === null);

  /**
   * WHAT IS NO LONGER HAPPENING, AND WHY — the studies called off over the reader's head.
   *
   * `sessions/mine` filtered to scheduled and live, so the two cancellations a reader does not make
   * — the community they held it under was deleted, or that community barred them — took the row
   * away with them. A weekly study simply stopped existing: no event, no message, nothing to read.
   * It is the same defect as being removed from a room and told nothing, and it has the same fix.
   *
   * Not filtered by node, unlike the list above: the point is that the study is GONE, so "go to the
   * node and look" is exactly what no longer works. Their own cancellations never reach here — the
   * server leaves them out, and `cancellationOf` would drop them anyway.
   */
  const calledOff = mine.filter(session => cancellationOf(session) !== null);

  /** The sentence for one, from core's shared rule so the phone cannot word it differently. */
  const whyOf = (session: SessionView): string => (
    cancellationOf(session) === 'hall-gone' ? t('sessions.hallGone') : t('sessions.notPermitted')
  );

  /** A session of the reader's own, named — its title, or the node it sits on. */
  const nameOfMine = (session: SessionView): string => session.title ?? nodeNameOf(session.nodeId);

  /** A session nobody named shows the node's name — read here, never stored on the session. */
  const nameOf = (session: SessionView): string => session.title ?? nodeName;

  /**
   * The reader's own locale and zone, which is the only correct way to show this: the server stores
   * an instant, and "Thursday 20:00" means 20:00 where the reader is sitting — and, inside the
   * hour, the DISTANCE instead, because that is the fact somebody is deciding on.
   *
   * The rule is shared with the Hall page, so the two panels that draw the same study cannot
   * describe it differently.
   */
  const whenOf = (session: SessionView): string => sessionWhenText(
    session.startsAt,
    lang,
    {
      unknown: t('shared.timeUnknown'),
      now: t('sessions.startingNow'),
      inMinutes: count => t('sessions.startsIn', { count: String(count) }),
      lasts: minutes => t('sessions.lasts', { count: String(minutes) }),
    },
    new Date(minuteTick),
    session.durationMinutes,
  );

  const schedule = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!when || busy) return;

    setNeedsSignIn(false);
    setProblem(null);
    setBusy(true);
    try {
      /* `datetime-local` gives a wall-clock string with no zone; `new Date` reads it as local time,
         which is exactly right — the leader means 20:00 where they are. */
      const startsAt = new Date(when);
      if (Number.isNaN(startsAt.getTime())) { setProblem(t('sessions.badTime')); return; }

      await client.scheduleSession(nodeId, {
        startsAt: startsAt.toISOString(),
        title: title.trim() || undefined,
        repeat: weekly ? 'weekly' : 'none',
        /* The promised length, which decides how long the room is HELD and when the study is
           considered over — not a cosmetic default. */
        durationMinutes: minutes,
        /* Absent, not null, for the commons — the DTO treats a missing field and a null differently. */
        hallId: underHall || undefined,
        /*
         * The zone this leader is standing in, so a WEEKLY study keeps its hour.
         * `startsAt` alone is an instant and cannot say which wall clock was meant: adding seven
         * days to it moves a 20:00 meeting to 19:00 the week the country leaves DST, permanently.
         */
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
        /*
         * WHICH DOOR (D24). A room has had three since rooms had doors and the calendar had one, so
         * a leader whose class is members-only could hold it privately only by opening the room by
         * hand at the hour — the one thing scheduling exists to save them.
         */
        access,
        passphrase: access === 'passphrase' ? passphrase : undefined,
      });
      setWhen('');
      setTitle('');
      setWeekly(false);
      setAccess('public');
      setPassphrase('');
      /* Outside the act, so a re-read that fails cannot be reported as a study that was not made. */
      await andReRead();
    } catch (caught) {
      if (caught instanceof SocialAuthError) setNeedsSignIn(true);
      /* The server says which of three things is wrong with a time — it is not a time, it is in the
         past, that is not a time zone — and a leader fixing a form needs to know which. A generic
         "that did not work" makes them guess at their own scheduled study. */
      else setProblem(refusalWords(t, caught, 'sessions.failed'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * A NEW WORD for a study held behind one — the rotation a room has had since it could lock.
   *
   * A leaked passphrase was permanent for the life of a weekly study: the only remedy was to cancel
   * it and schedule another, which tells the leader's community that the study is OFF and then
   * announces a second one, for a study that never stopped happening.
   *
   * The row opens the box rather than the panel carrying one: this is the rarest thing a leader
   * does here, and a passphrase field standing open beside every study would be furniture.
   */
  /**
   * RE-READ BOTH LISTS, AND NEVER LET A FAILED RE-READ LOOK LIKE A FAILED ACT.
   *
   * Scheduling, re-keying and cancelling each did `await act(); setSessions(await refresh())`
   * inside one `try`. While the client answered `[]` for a failed read that emptied the panel a
   * leader had just added to — their own study gone the moment they made it. Now that the read
   * says it failed, the same shape would report the ACT as failed instead, which is the other
   * wrong answer: a study is on the calendar and its author has been told it is not.
   *
   * So the two are separated, as they are on the bridge panel and the Hall page: the act keeps its
   * own sentence, and a re-read that fails leaves the lists saying they could not be read.
   */
  const andReRead = async (): Promise<void> => {
    try {
      setSessions(await refresh());
      setUnread(false);
    } catch {
      setUnread(true);
    }
    try {
      setMine(await refreshMine());
      setMineUnread(false);
    } catch {
      setMineUnread(true);
    }
  };

  const rekey = async (session: SessionView): Promise<void> => {
    setProblem(null);
    try {
      await client.rekeyStudy(session.id, newWord);
      setRekeying(null);
      setNewWord('');
      await andReRead();
    } catch (caught) {
      setProblem(refusalWords(t, caught, 'sessions.failed'));
    }
  };

  /*
   * ONE CALL-OFF PER STUDY. `DELETE sessions/:id` answers `no such scheduled session of yours`
   * once it is cancelled, so a second press earns a refusal BECAUSE THE FIRST WORKED — and this
   * panel says the refusal out loud. The row is on screen for the whole round trip.
   *
   * KEYED BY THE STUDY rather than by the button, because there are two buttons: this passage's
   * list and "yours, elsewhere". They never hold the SAME study — `elsewhere` excludes this node,
   * which a first version of the test for this assumed otherwise and had to be corrected — so the
   * key travels with the thing being acted on rather than with whichever control was pressed.
   */
  const row = useOneAtATime();

  const cancel = async (session: SessionView): Promise<void> => row.run(session.id, async () => {
    setProblem(null);
    try {
      await client.cancelSession(session.id);
      /* Both lists, because a session cancelled from either one has left both. */
      await andReRead();
    } catch (caught) {
      setProblem(refusalWords(t, caught, 'sessions.failed'));
    }
  });

  return (
    <section className="sessions" aria-label={t('sessions.label')}>
      <h3 className="panel-heading">{t('sessions.heading')}</h3>

      {sessions.length === 0 ? (
        <p className="sessions-empty" role={unread ? 'alert' : undefined}>
          {t(unread ? 'shared.couldNotRead' : 'sessions.empty')}
        </p>
      ) : (
        <ul className="sessions-list">
          {sessions.map(session => (
            <li key={session.id} className={session.status === 'live' ? 'session live' : 'session'}>
              <span className="session-title">{nameOf(session)}</span>
              <span className="session-when">{whenOf(session)}</span>
              {session.repeat === 'weekly' && <span className="session-repeat">{t('sessions.weekly')}</span>}
              {/* SAID IN THE LIST, not at the door. A reader deciding whether to plan their evening
                  around a study needs to know they will be asked for a word — the same badge a
                  locked room carries, for the same reason. */}
              {session.access === 'passphrase' && <span className="rooms-locked">{t('rooms.locked')}</span>}

              {/* A live session has a room, and the only useful thing to do with it is walk in. */}
              {session.status === 'live' && session.roomId && (
                <button type="button" className="session-join" onClick={() => onJoin(session.roomId ?? '')}>
                  {t('sessions.joinNow')}
                </button>
              )}

              {/* core's rule, which is the server's own filter written out on the client side —
                  see `mayCancelSession`. Shared with the phone, which offers the same control. */}
              {mayCancelSession(session, meId) && (
                /* Named, because there are now two lists in this panel and "Cancel" alone told a
                   screen reader nothing about WHICH promise it was about to undo. */
                <button
                  type="button"
                  className="session-cancel"
                  onClick={() => void cancel(session)}
                  disabled={row.working(session.id)}
                  aria-label={t('sessions.cancelThis', { name: nameOf(session) })}
                >
                  {t('sessions.cancel')}
                </button>
              )}

              {/* THE ROTATION, offered only to the owner of a study that HAS a word — and only in
                  the list, because a leaked passphrase is the rarest thing a leader deals with
                  here and a box beside every study would be furniture. */}
              {mayRekeyStudy(session, meId) && (
                rekeying === session.id ? (
                  <form
                    className="session-rekey"
                    onSubmit={event => { event.preventDefault(); void rekey(session); }}
                  >
                    <input
                      type="text"
                      className="session-new-word"
                      value={newWord}
                      onChange={event => setNewWord(event.target.value)}
                      placeholder={t('sessions.newPassphrase')}
                      aria-label={t('sessions.newPassphrase')}
                      minLength={MIN_PASSPHRASE}
                      maxLength={MAX_PASSPHRASE}
                    />
                    <button type="submit" className="session-rekey-save" disabled={newWord.length < MIN_PASSPHRASE}>
                      {t('sessions.changeIt')}
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="session-rekey-open"
                    onClick={() => { setRekeying(session.id); setNewWord(''); }}
                    aria-label={t('sessions.rekeyThis', { name: nameOf(session) })}
                  >
                    {t('sessions.newPassphrase')}
                  </button>
                )
              )}
            </li>
          ))}
        </ul>
      )}

      <form className="sessions-schedule" onSubmit={event => void schedule(event)}>
        <input
          type="datetime-local"
          className="sessions-when"
          value={when}
          onChange={event => setWhen(event.target.value)}
          aria-label={t('sessions.whenLabel')}
        />
        <input
          type="text"
          className="sessions-title"
          value={title}
          onChange={event => setTitle(event.target.value)}
          placeholder={t('sessions.titlePlaceholder')}
          aria-label={t('sessions.titlePlaceholder')}
          maxLength={SESSION_LIMITS.titleMaxLength}
        />
        {/* The same control as the rooms panel, and the same rule: absent for a reader in no Hall,
            commons by default. A leader's weekly study is the case it exists for. */}
        <HallPicker className="sessions-under-hall" halls={myHalls.halls} unread={myHalls.unread} value={underHall} onChange={setUnderHall} label={t('sessions.underHall')} />
        <label className="sessions-weekly">
          <input type="checkbox" checked={weekly} onChange={event => setWeekly(event.target.checked)} />
          {t('sessions.everyWeek')}
        </label>

        {/*
          HOW LONG IT RUNS — which no client could say, so every study was an hour.

          `durationMinutes` has been on the shape since sessions existed, the server clamps it to
          four hours, and no form sent it. That is not a cosmetic default: the promised length is
          what `holdUntil` keeps the room alive for, and what `endOverrunning` uses to decide the
          study is over — so a two-hour class was marked ENDED at sixty minutes, stopped being
          listed on its own node, and left everybody still in the room talking inside a study the
          product had already written off.

          A short list rather than a number box, because a leader knows the length of their own
          meeting and every calendar asks this way. The default stays an hour, so nothing that was
          scheduled before means anything different.
        */}
        <select
          className="sessions-length"
          value={String(minutes)}
          onChange={event => setMinutes(Number(event.target.value))}
          aria-label={t('sessions.howLong')}
        >
          {SESSION_LENGTHS.map(length => (
            <option key={length} value={String(length)}>{t('sessions.minutes', { count: String(length) })}</option>
          ))}
        </select>

        {/*
          WHO MAY COME IN when it opens (D24), and only two of the room's three doors: an invite
          code is minted per room and a weekly study opens a new room every occurrence, so "invite"
          would hand a community a fresh code every week that none of them had been given.
        */}
        <select
          className="sessions-access"
          value={access}
          onChange={event => setAccess(event.target.value === 'passphrase' ? 'passphrase' : 'public')}
          aria-label={t('sessions.whoMayCome')}
        >
          <option value="public">{t('rooms.accessPublic')}</option>
          <option value="passphrase">{t('rooms.accessPassphrase')}</option>
        </select>

        {/* Only where it means something: a passphrase box on a public study is a question with no
            consequence, and the server refuses a passphrase study without one. */}
        {access === 'passphrase' && (
          <input
            type="text"
            className="sessions-passphrase"
            value={passphrase}
            onChange={event => setPassphrase(event.target.value)}
            placeholder={t('sessions.setPassphrase')}
            aria-label={t('sessions.setPassphrase')}
            minLength={MIN_PASSPHRASE}
            maxLength={MAX_PASSPHRASE}
          />
        )}

        {/* The same floor the server holds to, so a leader is not offered a button that 400s. */}
        <button
          type="submit"
          className="sessions-add"
          disabled={busy || !when || (access === 'passphrase' && passphrase.length < MIN_PASSPHRASE)}
        >
          {busy ? t('sessions.scheduling') : t('sessions.schedule')}
        </button>
      </form>

      {/* WHAT YOU PROMISED ELSEWHERE. Absent for everyone who has promised nothing off this node,
          which is almost every reader — it appears exactly when it answers something. */}
      {mineUnread && <p className="sessions-empty" role="alert">{t('shared.couldNotRead')}</p>}
      {elsewhere.length > 0 && (
        <section className="sessions-mine" aria-label={t('sessions.yours')}>
          <h4 className="sessions-mine-heading">{t('sessions.yours')}</h4>
          <ul className="sessions-mine-list">
            {elsewhere.map(session => (
              <li key={session.id} className={session.status === 'live' ? 'sessions-mine-row live' : 'sessions-mine-row'}>
                <span className="session-title">{nameOfMine(session)}</span>
                {/* WHERE it is, and the way to get there. Joining is deliberately not offered from
                    here: this panel belongs to the node the reader is standing on, and connecting
                    them to a room somewhere else would leave the screen describing one place and
                    the microphone open in another. Going to the node is the honest move, and the
                    room controls are all waiting there. */}
                <button
                  type="button"
                  className="session-where"
                  onClick={() => onGoToNode(session.nodeId)}
                  aria-label={t('sessions.goThere', { name: nodeNameOf(session.nodeId) })}
                >
                  {nodeNameOf(session.nodeId)}
                </button>
                <span className="session-when">{whenOf(session)}</span>
                {/* The same badge the node's list carries, and the phone's. A leader looking at
                    their own studies should not have to remember which of them needs a word. */}
                {session.access === 'passphrase' && <span className="rooms-locked">{t('rooms.locked')}</span>}
                {/* Every session in THIS list is already the reader's own, so the ownership half is
                    a tautology here — asked anyway, so both lists cannot answer differently. */}
                {mayCancelSession(session, meId) && (
                  <button
                    type="button"
                    className="session-cancel"
                    onClick={() => void cancel(session)}
                    disabled={row.working(session.id)}
                    aria-label={t('sessions.cancelThis', { name: nameOfMine(session) })}
                  >
                    {t('sessions.cancel')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* WHAT SOMEBODY ELSE'S DECISION TOOK OFF THE CALENDAR. Absent for almost every reader,
          because almost nothing is ever called off this way — it appears exactly when it answers
          the question "where did my Thursday study go", which until now nothing answered. */}
      {calledOff.length > 0 && (
        <section className="sessions-called-off" aria-label={t('sessions.calledOff')}>
          <h4 className="sessions-mine-heading">{t('sessions.calledOff')}</h4>
          <ul className="sessions-mine-list">
            {calledOff.map(session => (
              <li key={session.id} className="sessions-mine-row called-off">
                <span className="session-title">{nameOfMine(session)}</span>
                <span className="session-when">{whenOf(session)}</span>
                {/* No control at all: there is nothing to undo, nothing to join, and nothing they
                    could press that would bring it back. It ages off the list in a week. */}
                <span className="session-why">{whyOf(session)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {needsSignIn && (
        <p className="sessions-signin" role="alert">
          <a href={client.signInUrl()}>{t('auth.google')}</a>{t('sessions.toSchedule')}
        </p>
      )}
      {problem && <p className="sessions-problem" role="alert">{problem}</p>}
    </section>
  );
}
